import { BaseComponent } from '../base-component/base-component';
import { ariaLabelWithNewTab } from '../../common/utils/a11y-util';
import { booleanConverter } from '../../common/converters';
import { customElement, property } from 'lit/decorators.js';
import { getDateFromTimestamp } from '../../common/utils/date-utils';
import { html, css, nothing } from 'lit';
import { CANCELATION_FREE_TRIAL_HELP_URL, isInactiveFreeTrial} from './utils/free-trial.util';
import { FreeTrialInfo } from './interfaces/free-trial-info.interface';
import emit from '../../common/events';

export enum Variant {
  small = 'small',
  large = 'large'
}

/**
 * A static element to display free trial subscription info.
 */
@customElement('mhecomm-free-trial-subscription-info')
export class FreeTrialSubscriptionInfo extends BaseComponent {
  static styles = [css`
      [part="free-trial-subscription-info-small"] {
          font-size: 0.875rem;
          line-height: 1.25rem;
          text-align: left
      }

      [part="free-trial-cancellation-info-large"] {
          line-height: 1.05rem;
          margin-top: 0.25rem;
      }
      
      [part="free-trial-subscription-info-large"] {
          font-size: 1rem;
          line-height: var(--theme-display-line-height);
          margin-top: 0.25rem;
      }
      
      @media (min-width: 768px) {
          [part="free-trial-subscription-info-large"] {
              float: right;
              font-size: 0.75rem;
              line-height: 0.9rem;
              max-width: 8.125rem;
          }
      }
      
      @media (min-width: 992px) {
          [part="free-trial-cancellation-info-large"] {
              float: right;
              max-width: 8.125rem;
              text-align: right;
          }
          [part="free-trial-subscription-info-large"] {
              float: right;
              font-size: 0.75rem;
              line-height: 0.9rem;
              max-width: 8.125rem;
          }
      }
  `];

  /**
   * A JSON String in the format of 'FreeTrialInfo'.
   */
  @property({type: Object})
  freeTrial?: FreeTrialInfo;

  /**
   * Col size for the verbiage
   */
  @property({type: String})
  colSize: string = "col-10";

  /**
   * If true, we will show the free trial cancellation info.
   */
  @property({converter: booleanConverter})
  showCancellationInfo = false;

  /**
   * If true, we will show the free trial cancellation terms.
   */
  @property({converter: booleanConverter})
  showCancellationTerms = false;

  /**
   * The variant of the component, it can be 'large' or 'small'.
   */
  @property()
  variant = Variant.small;

  /**
   * Renders price and billing date if the product is free trial.
   */
  renderComp() {
    return html`
      ${this.freeTrial?.isFreeTrial && this.freeTrial?.subscriptionUnitPrice 
        && this.freeTrial?.endDate && !isInactiveFreeTrial(this.freeTrial)    
        ? html`
          <div class=${this.colSize} part="free-trial-subscription-info-${this.variant}">
            $${this.freeTrial.subscriptionUnitPrice} plus taxes and fees billed on
            ${getDateFromTimestamp(this.freeTrial.endDate)}.
            ${this.showCancellationInfo
              ? html`
                  Free cancellation before end of trial.
                  ${this.renderCancellationTerms()}
                `
              : nothing
            }
          </div>`
        : nothing
      }
    `;
  }

   /**
   * Renders the cancellation terms and conditions for free trial products.
   *
   * @private
   */
  private renderCancellationTerms() {
    return this.showCancellationTerms
      ? html`
        <a
          href=${CANCELATION_FREE_TRIAL_HELP_URL}
          @click=${() => this.fireAnalytics('How do I cancel a free trial')}
          class="fs-small"
          aria-label="${ariaLabelWithNewTab('How do I cancel a free trial?')}"
          target="_blank"
        > How do I cancel a free trial? </a>`
      : nothing;
  }

  private fireAnalytics(eventName) {
    emit({
      element: this,
      name: 'click',
      detail: {
        label: eventName,
        eventName: eventName.toLowerCase().replace(/\s/g, '-'),
        url: window.location.href,
      },
    });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-free-trial-subscription-info': FreeTrialSubscriptionInfo;
  }
}
